import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { CandidateStage } from '@pms/shared';
import { cn } from '@/lib/utils';
import { initials, STAGE_META } from './utils';

export function Avatar({
  first, last, photoUrl, size = 'md',
}: {
  first?: string | null; last?: string | null; photoUrl?: string | null; size?: 'sm' | 'md' | 'lg';
}) {
  const dim = size === 'lg' ? 'h-12 w-12 text-base' : size === 'sm' ? 'h-8 w-8 text-[11px]' : 'h-10 w-10 text-sm';
  if (photoUrl) {
    return <img src={photoUrl} alt="" className={cn('shrink-0 rounded-full object-cover', dim)} />;
  }
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary', dim)}>
      {initials(first, last)}
    </div>
  );
}

export function StageBadge({ stage }: { stage: CandidateStage }) {
  const m = STAGE_META[stage];
  return (
    <span className={cn('inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium', m.className)}>
      {m.label}
    </span>
  );
}

/** Reusable right-side sheet (bottom sheet on mobile) built on Radix Dialog. */
export function RightSheet({
  open, onOpenChange, title, subtitle, children, footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-modal flex flex-col border-border/60 bg-background shadow-lg focus:outline-none',
            'inset-x-0 bottom-0 max-h-[90vh] rounded-t-xl border-t',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            'sm:inset-y-0 sm:bottom-0 sm:left-auto sm:right-0 sm:h-full sm:max-h-screen sm:w-full sm:max-w-lg sm:rounded-none sm:rounded-l-xl sm:border-l sm:border-t-0',
            'sm:data-[state=open]:slide-in-from-right sm:data-[state=closed]:slide-out-to-right',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="truncate text-base font-semibold text-foreground">
                {title}
              </DialogPrimitive.Title>
              {subtitle && (
                <DialogPrimitive.Description className="mt-0.5 truncate text-sm text-muted-foreground">
                  {subtitle}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close className="rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="border-t border-border/60 px-5 py-3">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
